import {
  WSActions,
  WSFlexBox,
  WSList,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import type { cardApiSpec } from "@wingspanhq/wingspan-api-types";
import { IAddress } from "@wingspanhq/users/dist/lib/interfaces";
import { useEffect, useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import {
  QUERY_CARDS,
  useQueryCards
} from "../../../../query/cards/queries/useQueryCards";
import { useUserId } from "../../../../query/hooks/helpers";
import { useWSMutation, WSQueryCache } from "../../../../query/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { ErrorContextKey } from "../../../../services/platform";
import {
  activateCard,
  createCard,
  getCards
} from "../../../../services/api/banking/cards";
import { FormCardAcknowledgements } from "../../../Wallet/components/FormCardAcknowledgements";
import { selectorCardShippingAddress } from "../../../Wallet/selectors/selectorCardShippingAddress";
import { WalletCardType } from "../../../Wallet/types";
import { selectorIsPendingWalletCard } from "../../selectors/selectorIsPendingWalletCard";
import { selectorVirtualWalletCard } from "../../selectors/selectorVirtualWalletCard";

const INVALID_ADDRESS_ERROR =
  "We don't have a valid address on file for you, so we can't create a card.";

type Props = {
  onBack: () => void;
  onOrdered: (card: cardApiSpec.Card) => void;
  // The user already has a virtual card this flow should not order or
  // activate, e.g. they came back here with the browser's Back button.
  onCardExists: () => void;
};

export const EmbeddedOrderVirtualCard: React.FC<Props> = props => {
  const userId = useUserId();
  const cardsQuery = useQueryCards();
  const memberProfileQuery = useQueryMemberProfile(userId);

  return (
    <WSQueries queries={{ cardsQuery, memberProfileQuery }}>
      {({ cardsQueryData, memberProfileQueryData }) => (
        <OrderVirtualCardSteps
          {...props}
          existingCard={selectorVirtualWalletCard(cardsQueryData)}
          address={memberProfileQueryData.profile.address}
        />
      )}
    </WSQueries>
  );
};

type StepsProps = Props & {
  existingCard?: cardApiSpec.Card;
  address?: IAddress;
};

const OrderVirtualCardSteps: React.FC<StepsProps> = props => {
  const userId = useUserId();
  const shippingAddress = selectorCardShippingAddress(props.address);
  // Only the card the user had on arrival decides the flow; the cards query
  // refetches after every order attempt.
  const [initialCard] = useState(props.existingCard);
  const isAlreadyOrdered =
    !!initialCard && !selectorIsPendingWalletCard(initialCard);
  const [isAcknowledged, setIsAcknowledged] = useState(!!initialCard);

  const [orderCard, orderCardMeta] = useWSMutation(async () => {
    try {
      // A failed attempt may still have created or activated the card on the
      // server, and creating a second one is rejected, so check first.
      const savedCard = selectorVirtualWalletCard(await getCards());

      if (savedCard && !selectorIsPendingWalletCard(savedCard)) {
        return savedCard;
      }

      const card =
        savedCard ??
        (await createCard({
          userId,
          isVirtualCard: true,
          address: shippingAddress!
        }));

      return await activateCard(card.cardId, {});
    } finally {
      // Refresh before leaving so the landing, and this route if the user
      // comes back to it, see the new card.
      await WSQueryCache.invalidateQueries(QUERY_CARDS);
    }
  });

  // Leaving from the mutation's onSuccess unmounts this component before the
  // mutation publishes its success state, which React reports as a state
  // update on an unmounted component. Leave once that state has rendered.
  const orderedCard = orderCardMeta.data;
  useEffect(() => {
    if (!orderedCard) return;
    // A suspended or one-time card found on the server was not ordered here.
    if (orderedCard.status === "Active") {
      props.onOrdered(orderedCard);
    } else {
      props.onCardExists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedCard]);

  useEffect(() => {
    if (isAlreadyOrdered) {
      props.onCardExists();
    } else if (initialCard && shippingAddress) {
      orderCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backButton = {
    label: "Back",
    kind: "Secondary" as const,
    onClick: props.onBack,
    type: "button" as const
  };

  if (isAlreadyOrdered) {
    return null;
  }

  if (!shippingAddress) {
    return (
      <WSList gap="XL">
        <WSErrorMessage
          error={INVALID_ADDRESS_ERROR}
          contextKey={ErrorContextKey.BankCard}
        />
        <WSActions alignment="fill" buttons={[backButton]} />
      </WSList>
    );
  }

  if (!isAcknowledged) {
    return (
      <FormCardAcknowledgements
        cardType={WalletCardType.Virtual}
        onBack={props.onBack}
        onContinue={() => {
          setIsAcknowledged(true);
          orderCard();
        }}
      />
    );
  }

  if (orderCardMeta.isError) {
    return (
      <WSList gap="XL">
        <WSErrorMessage
          error={orderCardMeta.error}
          contextKey={ErrorContextKey.BankCard}
        />
        <WSActions
          alignment="fill"
          buttons={[
            backButton,
            {
              label: "Try again",
              kind: "Primary",
              onClick: () => orderCard()
            }
          ]}
        />
      </WSList>
    );
  }

  return (
    <WSFlexBox.Center direction="column" gap="L">
      <WSLoader.Spinner />
      <WSText.ParagraphSm color="gray500">
        Creating your virtual card
      </WSText.ParagraphSm>
    </WSFlexBox.Center>
  );
};
